import React, {createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback} from "react";
import { useSearchParams} from "react-router-dom";
import {ToastType} from "./ToastMessage";
import {useAuth} from "../../context/user";

const subPages = [
    "active" ,
    "archived",
    "create",
    "new_import",
    "import_history",
    "alert",
    "message",
    "files",
    "upload",
    "log",
    "text-to-speech",
    "hotlines",
    "numbers",
    "create_number",
    "create_hotline",
    "list",
    "opt-ins",
] as const;

type SubPage =  typeof subPages[number];

interface SirenContextType {
    clientID: string | null;
    subMenuCurrent: SubPage;
    scopeParam: string | null;
    toastMessage: string | null;
    toastType: ToastType | undefined;
    setToast: (message: string | null, type: ToastType) => void;
}

const SirenContext = createContext<SirenContextType | null>(null);

export const SirenProvider = ({ children }: { children: ReactNode }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [toastType, setToastType] = useState<ToastType>()
    const {isSuperAdmin, clientID: userClientID} = useAuth()



    const clientID = useMemo(() => {
        if (isSuperAdmin) {
            const fromSearch = searchParams.get("clientID");
            if (fromSearch) return fromSearch;

            const fromScope = searchParams.get("scope");
            if (fromScope) {
                try {
                    const parsed = JSON.parse(fromScope);
                    if (parsed?.c) return parsed.c;
                } catch { /* ignore */ }
            }

            const fromStorage = localStorage.getItem("lastClientID");
            if (fromStorage) return fromStorage;
        }

        return userClientID;
    }, [isSuperAdmin, searchParams, userClientID]);

    // keep localStorage + searchParams in sync
    useEffect(() => {
        if (isSuperAdmin && clientID) {
            localStorage.setItem("lastClientID", clientID);

            if (searchParams.get("clientID") !== clientID) {
                const newParams = new URLSearchParams(searchParams);
                newParams.set("clientID", clientID);
                setSearchParams(newParams, { replace: true });
            }
        }
    }, [isSuperAdmin, clientID, searchParams, setSearchParams]);

    useEffect(() => {
        if (toastMessage) {
            const timer = setTimeout(() => {
                setToastMessage(null);
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [toastMessage]);

    const subMenuCurrent = useMemo<SubPage>(() => {
        const val = searchParams.get("subPage")
        if(subPages.includes(val as SubPage)) {
            return val as SubPage
        }
        return "active" as SubPage
    }, [searchParams])

    const scopeParam = useMemo(() => searchParams.get("scope"), [searchParams]);

    const setToast = useCallback((message: string | null, type: ToastType) => {
        setToastMessage(message);
        setToastType(type)
    },[])

    const value: SirenContextType = {
        clientID,
        subMenuCurrent,
        scopeParam,
        toastMessage,
        toastType,
        setToast,
    };

    return (
        <SirenContext.Provider value={value}>
            {children}
        </SirenContext.Provider>
    );
};

export const useSiren = (): SirenContextType => {
    const context = useContext(SirenContext);
    if (!context) {
        throw new Error("useSiren must be used within a SirenProvider");
    }
    return context;
};